import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { getBaseUrl } from '@/utils/api';

const API_URL = getBaseUrl();

// Helper function to make XHR requests (avoids fetch interceptor issues)
const xhrRequest = (method, url, data) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.withCredentials = true;
    
    xhr.onload = function() {
      try {
        const responseData = JSON.parse(xhr.responseText);
        resolve({ ok: xhr.status < 400, status: xhr.status, data: responseData });
      } catch (e) {
        reject(new Error('Failed to parse response'));
      }
    };
    
    xhr.onerror = function() {
      reject(new Error('Network error'));
    };
    
    xhr.send(data ? JSON.stringify(data) : null);
  });
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useUser();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // If already logged in, redirect appropriately
    if (user) {
      if (user.role === 'master_admin') {
        navigate('/admin/dashboard', { replace: true });
      } else if (user.role === 'coach') {
        navigate('/coach/dashboard', { replace: true });
      } else {
        navigate('/client/hub', { replace: true });
      }
      return;
    }
    
    // Use ref to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          console.error('No session_id in URL');
          navigate('/');
          return;
        }

        // Check for registration intent (from welcome page with coach info)
        const registrationIntentStr = sessionStorage.getItem('google_registration_intent');
        const registrationIntent = registrationIntentStr ? JSON.parse(registrationIntentStr) : null;
        
        // Clear the intent immediately
        sessionStorage.removeItem('google_registration_intent');

        // If there's a registration intent with coach info, try to register first
        if (registrationIntent && registrationIntent.coachName && registrationIntent.coachIdKey) {
          try {
            const registerResult = await xhrRequest('POST', `${API_URL}/api/auth/google/register`, {
              session_id: sessionId,
              role: 'client',
              coach_name: registrationIntent.coachName,
              coach_id_key: registrationIntent.coachIdKey
            });

            if (registerResult.ok) {
              login(registerResult.data.user, registerResult.data.access_token);
              navigate('/client/hub', { replace: true });
              return;
            }
            
            // If registration failed but user already exists, try login
            if (!registerResult.data.detail?.includes('already registered')) {
              console.error('Registration error:', registerResult.data.detail);
              sessionStorage.setItem('auth_message', registerResult.data.detail || 'Registration failed. Please try again.');
              navigate('/', { replace: true });
              return;
            }
          } catch (err) {
            console.error('Registration request failed:', err);
          }
        }

        // Try to login with Google (existing user or user without registration intent)
        try {
          const loginResult = await xhrRequest('POST', `${API_URL}/api/auth/google`, {
            session_id: sessionId
          });

          if (loginResult.ok) {
            login(loginResult.data.user, loginResult.data.access_token);

            // Navigate based on role
            if (loginResult.data.user.role === 'master_admin') {
              navigate('/admin/dashboard', { replace: true });
            } else if (loginResult.data.user.role === 'coach') {
              navigate('/coach/dashboard', { replace: true });
            } else {
              navigate('/client/hub', { replace: true });
            }
            return;
          }

          // Login failed
          console.error('Auth error:', loginResult.data.detail);
          
          if (loginResult.data.detail?.includes('not found') || loginResult.data.detail?.includes('sign up')) {
            // User not found - they need to register with coach info
            sessionStorage.setItem('auth_message', 'No account found. Please enter your coach information to create an account.');
            navigate('/', { replace: true });
          } else if (loginResult.data.detail?.includes('whitelist')) {
            // Email not whitelisted
            sessionStorage.setItem('auth_message', loginResult.data.detail);
            navigate('/', { replace: true });
          } else {
            sessionStorage.setItem('auth_message', loginResult.data.detail || 'Authentication failed. Please try again.');
            navigate('/', { replace: true });
          }
        } catch (err) {
          console.error('Login request failed:', err);
          sessionStorage.setItem('auth_message', 'Authentication failed. Please try again.');
          navigate('/', { replace: true });
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/');
      }
    };

    processAuth();
  }, [location, navigate, login, user]);

  return (
    <div className="min-h-screen bg-ivory-mist flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-burnt-terra mx-auto mb-4"></div>
        <p className="text-shadow-pine font-body">Signing you in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
